import { useEffect, useState } from 'react';
import { Sparkles, Check } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INTERESTS = [
  { key: 'lokalne-vijesti', label: 'Lokalne vijesti' },
  { key: 'sport', label: 'Sport' },
  { key: 'kultura', label: 'Kultura i događanja' },
  { key: 'gospodarstvo', label: 'Gospodarstvo' },
  { key: 'politika', label: 'Politika' },
  { key: 'obrazovanje', label: 'Obrazovanje' },
  { key: 'zdravstvo', label: 'Zdravstvo' },
  { key: 'promet', label: 'Promet' },
  { key: 'crna-kronika', label: 'Crna kronika' },
  { key: 'turizam', label: 'Turizam' },
  { key: 'gastronomija', label: 'Gastronomija' },
  { key: 'eu-projekti', label: 'EU projekti' },
  { key: 'kripto', label: 'Kripto' },
  { key: 'svijet', label: 'Svijet' },
  { key: 'medjimurje', label: 'Međimurje' },
  { key: 'varazdin', label: 'Varaždin' },
];

const STORAGE_KEY = 'emina_interests_prompted_v1';
const PRIJAVA_INTERESTS_KEY = 'emina_prijava_interests_pending_v1';

/**
 * Onboarding interests modal — shown to authenticated users who have not
 * yet provided their interests. Persists selection to /api/public/me/interests
 * for newsletter segmentation.
 */
export default function InterestsOnboardingModal() {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0();
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) return;
    const seen = (() => { try { return localStorage.getItem(STORAGE_KEY); } catch { return null; } })();
    if (seen) return;
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const r = await fetch(`${API}/public/me/interests`, { headers: { Authorization: `Bearer ${token}` } });
        if (!r.ok) return;
        const d = await r.json();
        const userInterests = (d.interests || []);

        // Pull any pre-picked interests from the /prijava page
        let prijavaPicks = [];
        try {
          const raw = localStorage.getItem(PRIJAVA_INTERESTS_KEY);
          prijavaPicks = raw ? JSON.parse(raw) : [];
        } catch {}

        // If user pre-picked interests on /prijava and currently has none, auto-save
        if (!userInterests.length && prijavaPicks.length) {
          await fetch(`${API}/public/me/interests`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ interests: prijavaPicks }),
          });
          toast.success('Spremili smo tvoje interese odabrane na prijavi!');
          try {
            localStorage.removeItem(PRIJAVA_INTERESTS_KEY);
            localStorage.setItem(STORAGE_KEY, '1');
          } catch {}
          return;
        }

        if (!userInterests.length) {
          // Pre-fill modal with /prijava picks if present
          if (prijavaPicks.length) setPicked(new Set(prijavaPicks));
          setTimeout(() => setOpen(true), 1200);
        }
      } catch {}
    })();
  }, [isAuthenticated, getAccessTokenSilently]);

  function toggle(k) {
    setPicked(prev => {
      const n = new Set(prev);
      if (n.has(k)) n.delete(k); else n.add(k);
      return n;
    });
  }

  function dismissForever() {
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
    setOpen(false);
  }

  async function save() {
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      await fetch(`${API}/public/me/interests`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ interests: Array.from(picked) }),
      });
      toast.success('Hvala! Newsletter će biti prilagođen tvojim interesima.');
      dismissForever();
    } catch {
      toast.error('Greška pri spremanju.');
    } finally { setBusy(false); }
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 flex items-end sm:items-center justify-center p-4 backdrop-blur-sm"
         data-testid="interests-modal">
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-2xl shadow-2xl w-full max-w-2xl">
        <div className="p-6">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="text-amber-500" size={22}/>
            <h2 className="text-xl font-extrabold tracking-tight" data-testid="interests-title">Što te najviše zanima?</h2>
          </div>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-4">
            Odaberi nekoliko tema kako bismo ti slali samo vijesti koje su ti relevantne.
            Možeš mijenjati izbor u svakom trenutku.
          </p>
          <div className="flex flex-wrap gap-2 mb-5">
            {INTERESTS.map(i => {
              const on = picked.has(i.key);
              return (
                <button key={i.key} onClick={() => toggle(i.key)} type="button"
                        data-testid={`interest-${i.key}`}
                        className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${on
                          ? 'bg-red-600 border-red-600 text-white'
                          : 'border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-200 hover:border-red-500 hover:text-red-600'}`}>
                  {on && <Check size={12}/>}
                  {i.label}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2">
            <button onClick={dismissForever} data-testid="interests-skip"
                    className="text-xs text-slate-500 hover:text-slate-700 dark:hover:text-slate-300">
              Preskoči za sada
            </button>
            <button onClick={save} disabled={busy || picked.size === 0}
                    data-testid="interests-save"
                    className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-bold disabled:opacity-50">
              {busy ? 'Spremam…' : `Spremi (${picked.size})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
